import type {
  UpdateActionTypePayload,
  UpdateActionTypeResponse,
} from '@/domains/actionType/actionType.entity';
import type { ActionTypeRepository } from '@/domains/actionType/actionType.repository';

/**
 * Use Case para actualizar un tipo de acción existente en el sistema.
 *
 * Funcionalidad:
 * - Modifica los datos de un tipo de acción previamente registrado
 * - Permite actualizar descripción, estado u otros atributos del tipo de acción
 * - Persiste los cambios mediante el repositorio
 *
 * Responsabilidades:
 * - Validar el código del tipo de acción y el payload antes de actualizar
 * - Coordinar la actualización con el repositorio
 * - Actuar como intermediario entre dominio e infraestructura
 * - Asegurar que los cambios cumplan con las reglas de negocio
 */
export class UpdateActionTypeUseCase {
  private repository: ActionTypeRepository;

  /**
   * Inicializa el UseCase con las dependencias necesarias.
   *
   * @param repository - Repositorio que implementa ActionTypeRepository para operaciones CRUD
   */
  constructor(repository: ActionTypeRepository) {
    this.repository = repository;
  }

  /**
   * Ejecuta la actualización de un tipo de acción existente.
   *
   * @param cod_tipo_accion - Código único del tipo de acción a actualizar
   * @param payload - Datos actualizados del tipo de acción
   */
  async execute(
    cod_tipo_accion: number,
    payload: UpdateActionTypePayload,
  ): Promise<UpdateActionTypeResponse> {
    return await this.repository.updateActionType(cod_tipo_accion, payload);
  }
}
